/* eslint-disable @typescript-eslint/no-unused-vars, react-refresh/only-export-components -- pre-existing in this file; the change here only swaps the selection checkbox for the design-system one */
import { forwardRef } from 'react';
import { Checkbox } from '@kula-ai/components';
import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useDisclosure,
  Portal,
} from '@chakra-ui/react';
import { KText } from '@/ui-library/components';

// The design-system checkbox (both themes): the bare native input drew the
// browser's own pale-blue box, unlike every other checkbox in the app. kula's
// Checkbox takes the same input props and handles `indeterminate` itself.
export const IndeterminateCheckbox = forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement> & { indeterminate?: boolean }
>(({ indeterminate, ...rest }, ref) => (
  <Checkbox
    ref={ref}
    size="sm"
    indeterminate={indeterminate || false}
    {...(rest as Omit<typeof rest, 'size' | 'type'>)}
  />
));
IndeterminateCheckbox.displayName = 'IndeterminateCheckbox';

const SelectionHeader = ({
  getToggleAllRowsSelectedProps,
  state,
  rows,
  totalCount,
  pageLimit,
  handleSelectionState,
  resetValue,
}) => {
  const allProps = getToggleAllRowsSelectedProps();
  const selectedCount = Object.keys(state.selectedRowIds).length;
  const rowCount = rows.length;
  const isIndeterminate = selectedCount > 0 && selectedCount < rowCount;
  const isChecked = selectedCount > 0 && selectedCount === rowCount;

  // Add menu state hooks
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Add these functions for menu item actions
  const handleSelectInPage = () => {
    // Select all rows in the current page
    rows.forEach((row) => {
      row.toggleRowSelected(true);
    });
    handleSelectionState('inPage');
  };

  const handleSelectAll = () => {
    // Select all rows
    rows.forEach((row) => {
      row.toggleRowSelected(true);
    });
    handleSelectionState('all');
  };

  const handleDeselectAll = () => {
    // Deselect all rows
    rows.forEach((row) => {
      row.toggleRowSelected(false);
    });
    resetValue();
  };

  const handleChange = (e) => {
    e.stopPropagation();
    if (!isChecked && !isIndeterminate) {
      rows.forEach((row) => {
        row.toggleRowSelected(true);
      });
      handleSelectionState('inPage');
    } else {
      if (isIndeterminate) {
        handleDeselectAll();
      } else {
        // Toggle all rows selected
        if (isChecked) {
          handleDeselectAll();
        } else {
          handleSelectInPage();
        }
      }
    }
  };

  return (
    <div>
      {pageLimit < totalCount ? (
        <Menu isOpen={isOpen} onClose={onClose}>
          <MenuButton
            // onMouseEnter={onOpen}
            onClick={(e) => {
              e.stopPropagation();
              if (!isChecked && !isIndeterminate) {
                onOpen();
              } else {
                if (isIndeterminate) {
                  handleDeselectAll();
                } else {
                  // Toggle all rows selected
                  if (isChecked) {
                    handleDeselectAll();
                  } else {
                    handleSelectInPage();
                  }
                }
              }
            }}
          >
            <IndeterminateCheckbox
              {...allProps}
              checked={isChecked}
              indeterminate={isIndeterminate}
            />
          </MenuButton>
          <Portal>
            <MenuList width="fit-content" zIndex="999">
              <MenuItem onClick={handleSelectInPage}>
                <KText>
                  In page <strong>({rows.length})</strong>
                </KText>
              </MenuItem>
              <MenuItem onClick={handleSelectAll}>
                <KText>
                  All candidates <strong>({totalCount})</strong>
                </KText>
              </MenuItem>
            </MenuList>
          </Portal>
        </Menu>
      ) : (
        <IndeterminateCheckbox
          {...allProps}
          checked={isChecked}
          indeterminate={isIndeterminate}
          onChange={handleChange}
        />
      )}
    </div>
  );
};

const SelectionCell = ({ row, state }: { row: any; state: any }) => {
  const rowProps = row.getToggleRowSelectedProps();

  return (
    <div>
      <IndeterminateCheckbox {...rowProps} />
    </div>
  );
};

export function useSelectAllColumn(hooks) {
  hooks.visibleColumns.push((columns, { instance }) => {
    return [
      {
        id: 'selection',
        disableResizing: true,
        disableGroupBy: true,
        disableSortBy: true,
        disableFilters: true,
        width: 40,
        Header: ({ getToggleAllRowsSelectedProps, state, rows }) => {
          const {
            totalCount: latestTotalCount = 0,
            pageLimit = 0,
            handleSelectionState = () => {},
            resetValue = () => {},
          } = instance.selectAllColumnOptions || {};

          return (
            <SelectionHeader
              getToggleAllRowsSelectedProps={getToggleAllRowsSelectedProps}
              state={state}
              rows={rows}
              totalCount={latestTotalCount}
              pageLimit={pageLimit}
              handleSelectionState={handleSelectionState}
              resetValue={resetValue}
            />
          );
        },
        Cell: ({ row, state }) => <SelectionCell row={row} state={state} />,
      },
      ...columns,
    ];
  });
}
